import { useMemo, useState } from "react";
import { Search, Plus, Users, Building2 } from "lucide-react";
import { Avatar } from "@/components/chat/Avatar";

const time = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toDateString() === new Date().toDateString()
        ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export function ConversationList({ convs, activeId, online, onOpen, onNewChat, onNewGroup, onDeptGroups, canGroup }) {
    const [q, setQ] = useState("");
    const rows = useMemo(() => convs.filter((c) => c.title.toLowerCase().includes(q.trim().toLowerCase())), [convs, q]);
    return (
        <div className="flex flex-col h-full" data-testid="chat-conversation-list">
            <div className="p-4 border-b border-ink/10 space-y-3">
                <div className="flex items-center justify-between">
                    <h1 className="font-display font-extrabold text-2xl text-ink">Chat</h1>
                    <div className="flex gap-1.5">
                        {canGroup && (
                            <button onClick={onDeptGroups} className="p-2 rounded-full border border-ink/15 hover:bg-ink/5" title="Create a group for every department" data-testid="chat-dept-groups-btn"><Building2 className="w-4 h-4" /></button>
                        )}
                        {canGroup && (
                            <button onClick={onNewGroup} className="p-2 rounded-full border border-ink/15 hover:bg-ink/5" title="New group" data-testid="chat-new-group-btn"><Users className="w-4 h-4" /></button>
                        )}
                        <button onClick={onNewChat} className="p-2 rounded-full bg-klein text-white hover:bg-klein/90" title="New chat" data-testid="chat-new-chat-btn"><Plus className="w-4 h-4" /></button>
                    </div>
                </div>
                <label className="flex items-center gap-2 border border-ink/15 rounded-full px-3 py-2 focus-within:border-klein">
                    <Search className="w-4 h-4 text-ink/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats" className="flex-1 bg-transparent text-sm outline-none" data-testid="chat-search-input" />
                </label>
            </div>
            <div className="flex-1 overflow-y-auto">
                {rows.length === 0 && (
                    <div className="p-8 text-center text-sm text-ink/45" data-testid="chat-list-empty">
                        No chats yet. Tap <b>+</b> to message a colleague.
                    </div>
                )}
                {rows.map((c) => (
                    <button key={c.id} onClick={() => onOpen(c.id)} data-testid={`chat-conv-${c.id}`}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-ink/5 transition-colors ${activeId === c.id ? "bg-klein/5" : "hover:bg-ink/[0.03]"}`}>
                        <Avatar name={c.title} group={c.type !== "direct"} announce={c.type === "announcement"} online={c.type === "direct" ? online.has(c.other?.id) : undefined} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <span className={`truncate text-sm ${c.unread ? "font-bold text-ink" : "font-medium text-ink/85"}`}>{c.title}</span>
                                <span className="text-[11px] text-ink/40 shrink-0">{time(c.last_message_at)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-xs text-ink/50">{c.last_message_preview || (c.type === "group" ? `${c.members.length} members` : "Say hello")}</span>
                                {c.unread > 0 && <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-klein text-white text-[11px] font-bold flex items-center justify-center" data-testid={`chat-unread-${c.id}`}>{c.unread}</span>}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default ConversationList;
